import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Clock3, History, Phone, PhoneCall, UserRoundCheck } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ACTIONS, RESOURCES, ROLES } from '../config/accessControl';
import {
  CALL_RESULT_LABELS,
  CALL_RESULTS,
  SHEET_STATUS_META,
} from '../config/sheetStatus';
import { useAuth } from '../context/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { callControlService } from '../services';
import { sheetService } from '../services';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import EmptyState from '../components/common/EmptyState';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import Checkbox from '../components/forms/Checkbox';
import Input from '../components/forms/Input';
import Select from '../components/forms/Select';
import Textarea from '../components/forms/Textarea';
import Table from '../components/tables/Table';

const formatDate = (value, withTime = true) =>
  value
    ? new Intl.DateTimeFormat('es-PY', withTime
      ? { dateStyle: 'short', timeStyle: 'short' }
      : { dateStyle: 'medium' }).format(new Date(value))
    : '—';

const followUpResults = [
  CALL_RESULTS.PENDING,
  CALL_RESULTS.CALL_BACK,
  CALL_RESULTS.NO_ANSWER,
  CALL_RESULTS.OUT_OF_SERVICE,
];

function ResultBadge({ result }) {
  if (!result) return <Badge variant="warning">Pendiente</Badge>;
  const variant = result === CALL_RESULTS.CONFIRMED
    ? 'success'
    : [CALL_RESULTS.NOT_INTERESTED, CALL_RESULTS.NOT_APPLICABLE, CALL_RESULTS.WRONG_NUMBER]
      .includes(result)
      ? 'danger'
      : followUpResults.includes(result) ? 'warning' : 'info';
  return <Badge variant={variant}>{CALL_RESULT_LABELS[result] || result}</Badge>;
}

function Metric({ label, value, detail }) {
  return (
    <Card>
      <p className="text-sm text-content-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-content-primary">{value}</p>
      {detail && <p className="mt-1 text-xs text-content-muted">{detail}</p>}
    </Card>
  );
}

function CallSheetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const [sheet, setSheet] = useState(null);
  const [voters, setVoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [attemptFilter, setAttemptFilter] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [selectedVoter, setSelectedVoter] = useState(null);
  const [historyVoter, setHistoryVoter] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [result, setResult] = useState(CALL_RESULTS.CONFIRMED);
  const [observation, setObservation] = useState('');
  const [nextCallDate, setNextCallDate] = useState('');
  const [saving, setSaving] = useState(false);

  const canCall = can(RESOURCES.CALL_SHEETS, ACTIONS.CALL) &&
    [ROLES.ADMINISTRADOR, ROLES.ADMINISTRADOR_GENERAL, ROLES.COORDINADOR].includes(user.role);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [sheetResponse, votersResponse] = await Promise.all([
        sheetService.getDetailForActor(id, user),
        sheetService.getVoters(id),
      ]);
      setSheet(sheetResponse.data);
      setVoters(votersResponse.data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredVoters = useMemo(() => voters.filter((voter) => {
    const lastResult = voter.lastCall?.result || CALL_RESULTS.PENDING;
    const isPending = !voter.lastCall || followUpResults.includes(lastResult);
    return (
      (!resultFilter || lastResult === resultFilter) &&
      (!attemptFilter || (
        attemptFilter === '3+'
          ? voter.attemptCount >= 3
          : voter.attemptCount === Number(attemptFilter)
      )) &&
      (!pendingOnly || isPending)
    );
  }), [attemptFilter, pendingOnly, resultFilter, voters]);

  const openCall = (voter) => {
    setSelectedVoter(voter);
    setPhoneNumber(voter.phone || '');
    setResult(CALL_RESULTS.CONFIRMED);
    setObservation('');
    setNextCallDate('');
    setError('');
  };

  const submitCall = async () => {
    setSaving(true);
    setError('');
    try {
      await callControlService.createForSheet(id, {
        voterId: selectedVoter.id,
        phoneNumber,
        result,
        observation,
        nextCallDate,
      }, user);
      setSelectedVoter(null);
      setFeedback('Llamada registrada como un nuevo intento.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (voter) => {
    setHistoryVoter(voter);
    setHistoryLoading(true);
    setError('');
    try {
      const response = await callControlService.getHistory(id, voter.id, user);
      setHistory(response.data);
    } catch (requestError) {
      setError(requestError.message);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const leaderColumns = [
    { key: 'document', header: 'Cédula' },
    { key: 'name', header: 'Nombre completo', render: (value) => <span className="font-medium text-content-primary">{value}</span> },
    {
      key: 'phone',
      header: 'Teléfono',
      render: (value) => value ? (
        <a href={`tel:${String(value).replace(/\s/g, '')}`} className="inline-flex items-center gap-1.5 font-medium text-primary-700 hover:underline">
          <Phone className="size-3.5" /> {value}
        </a>
      ) : 'Sin teléfono',
    },
    { key: 'voting_location', header: 'Local de votación', render: (value) => value || '—' },
    { key: 'voting_table', header: 'Mesa', render: (value) => value || '—' },
  ];

  const managementColumns = [
    ...leaderColumns,
    { key: 'lastCall', header: 'Última llamada', render: (value) => formatDate(value?.called_at || value?.calledAt) },
    { key: 'attemptCount', header: 'Intentos', align: 'right' },
    { key: 'result', header: 'Último resultado', render: (_, voter) => <ResultBadge result={voter.lastCall?.result} /> },
    {
      key: 'observation',
      header: 'Observación',
      render: (_, voter) => (
        <span className="line-clamp-2 max-w-48 text-xs">
          {voter.lastCall?.observation || voter.lastCall?.notes || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      render: (_, voter) => {
        const isPending = !voter.lastCall || followUpResults.includes(voter.lastCall.result);
        return <Badge variant={isPending ? 'warning' : 'success'}>{isPending ? 'Pendiente' : 'Gestionado'}</Badge>;
      },
    },
    {
      key: 'actions',
      header: 'Acciones',
      render: (_, voter) => (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => openHistory(voter)}>
            <History className="size-4" /> Historial
          </Button>
          {canCall && (
            <Button variant="outline" size="sm" onClick={() => openCall(voter)}>
              <PhoneCall className="size-4" /> Registrar llamada
            </Button>
          )}
        </div>
      ),
    },
  ];
  const columns = user.role === ROLES.PUNTERO ? leaderColumns : managementColumns;

  if (loading) return <div className="grid min-h-96 place-items-center"><LoadingSpinner label="Cargando planilla" /></div>;
  if (!sheet) return (
    <EmptyState
      icon={PhoneCall}
      title="No se pudo abrir la planilla"
      description={error || 'La planilla no existe o está fuera de tu estructura.'}
      actionLabel="Volver a planillas"
      onAction={() => navigate('/control-planillas')}
    />
  );

  const calledPercentage = sheet.total_votantes
    ? Math.round((sheet.total_llamados / sheet.total_votantes) * 100)
    : 0;
  const statusMeta = SHEET_STATUS_META[sheet.validation_status];

  return (
    <div>
      <Link to="/control-planillas" className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:underline">
        <ArrowLeft className="size-4" /> Volver a planillas
      </Link>
      <div className="mb-7 flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Badge variant={statusMeta?.variant || 'neutral'} dot>{statusMeta?.label || sheet.validation_status}</Badge>
            <Badge variant="neutral">{sheet.campaignName}</Badge>
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-content-primary">{sheet.punteroName}</h2>
          <p className="mt-1 text-sm text-content-muted">Coordinador responsable: {sheet.coordinadorName}</p>
        </div>
        <p className="text-sm text-content-muted">Última llamada: {formatDate(sheet.last_call_at)}</p>
      </div>

      {feedback && <Alert variant="success" title="Llamada guardada" className="mb-5">{feedback}</Alert>}
      {error && !selectedVoter && !historyVoter && <Alert variant="danger" title="No se pudo completar" className="mb-5">{error}</Alert>}
      {sheet.validation_observation && <Alert variant="info" title="Observación de validación" className="mb-5">{sheet.validation_observation}</Alert>}

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label="Total de votantes" value={sheet.total_votantes} detail={`${sheet.total_llamados} con al menos un intento`} />
        <Metric label="Porcentaje llamado" value={`${calledPercentage}%`} detail={`${sheet.total_votantes - sheet.total_llamados} todavía sin llamar`} />
        <Metric label="Porcentaje de respuesta" value={`${sheet.response_percentage}%`} detail={`${sheet.total_contactados} contestaron`} />
        <Metric label="Estado de validación" value={statusMeta?.label || sheet.validation_status} detail={sheet.validated_at ? `Validada el ${formatDate(sheet.validated_at, false)}` : 'Aún sin validación final'} />
      </div>

      {user.role !== ROLES.PUNTERO && <Card title="Filtros de llamadas" className="mb-5">
        <div className="grid items-end gap-4 sm:grid-cols-3">
          <Select id="call-result-filter" label="Resultado" value={resultFilter} onChange={(event) => setResultFilter(event.target.value)} options={Object.entries(CALL_RESULT_LABELS).map(([value, label]) => ({ value, label }))} placeholder="Todos los resultados" />
          <Select id="call-attempt-filter" label="Cantidad de intentos" value={attemptFilter} onChange={(event) => setAttemptFilter(event.target.value)} options={[{ value: '0', label: 'Sin intentos' }, { value: '1', label: '1 intento' }, { value: '2', label: '2 intentos' }, { value: '3+', label: '3 o más intentos' }]} placeholder="Cualquier cantidad" />
          <Checkbox id="pending-only" label="Ver únicamente pendientes" description="Votantes sin un resultado de llamada registrado." checked={pendingOnly} onChange={(event) => setPendingOnly(event.target.checked)} />
        </div>
      </Card>}

      {filteredVoters.length ? (
        <Table columns={columns} data={filteredVoters} emptyMessage="No hay votantes con estos filtros." />
      ) : (
        <EmptyState icon={UserRoundCheck} title="Sin coincidencias" description="No hay votantes que cumplan los filtros seleccionados." />
      )}

      <Modal open={Boolean(selectedVoter)} onClose={() => !saving && setSelectedVoter(null)} size="md" title="Registrar llamada" description={selectedVoter ? `${selectedVoter.name} · intento ${selectedVoter.attemptCount + 1}` : ''} footer={<><Button variant="ghost" onClick={() => setSelectedVoter(null)}>Cancelar</Button><Button onClick={submitCall} loading={saving}><PhoneCall className="size-4" /> Guardar intento</Button></>}>
        {selectedVoter && (
          <div className="space-y-4">
            {error && <Alert variant="danger" title="No se pudo registrar">{error}</Alert>}
            <div className="flex items-end gap-3">
              <Input id="call-phone-number" label="Número utilizado" className="flex-1" value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} required />
              {phoneNumber && <a href={`tel:${phoneNumber.replace(/\s/g, '')}`} className="inline-flex h-10 items-center gap-2 rounded-control bg-success-600 px-4 text-sm font-semibold text-white hover:bg-success-700"><Phone className="size-4" /> Llamar</a>}
            </div>
            <Select id="call-detail-result" label="Resultado" value={result} onChange={(event) => setResult(event.target.value)} options={Object.entries(CALL_RESULT_LABELS).map(([value, label]) => ({ value, label }))} />
            <Textarea id="call-detail-observation" label="Observación" value={observation} onChange={(event) => setObservation(event.target.value)} />
            <Input id="call-next-date" type="date" label="Programar próxima llamada" value={nextCallDate} onChange={(event) => setNextCallDate(event.target.value)} hint="Opcional. Puede utilizarse para organizar un nuevo intento." />
          </div>
        )}
      </Modal>

      <Modal open={Boolean(historyVoter)} onClose={() => setHistoryVoter(null)} size="lg" title="Historial de llamadas" description={historyVoter ? historyVoter.name : ''}>
        {error && <Alert variant="danger" title="No se pudo cargar el historial">{error}</Alert>}
        {historyLoading ? <LoadingSpinner label="Cargando historial" /> : history.length ? (
          <div className="space-y-3">
            {history.map((call) => (
              <div key={call.id} className="rounded-control border p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2"><Badge variant="neutral">Intento {call.attempt_number}</Badge><ResultBadge result={call.result} /></div>
                  <span className="text-xs text-content-muted">{formatDate(call.called_at)}</span>
                </div>
                <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                  <div><dt className="text-xs text-content-muted">Número utilizado</dt><dd>{call.phone_number || '—'}</dd></div>
                  <div><dt className="text-xs text-content-muted">Próxima llamada</dt><dd>{formatDate(call.next_call_date, false)}</dd></div>
                  <div className="sm:col-span-2"><dt className="text-xs text-content-muted">Observación</dt><dd>{call.observation || 'Sin observación'}</dd></div>
                </dl>
              </div>
            ))}
          </div>
        ) : <EmptyState icon={Clock3} title="Sin intentos registrados" description="Este votante todavía no tiene historial de llamadas." />}
      </Modal>
    </div>
  );
}

export default CallSheetDetailPage;
